import { Button } from "@/components/ui/button";
import { ArrowDown, Sparkles, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLang } from "@/contexts/LanguageContext";

const tunisianCollage = "/tunisian-collage-opt.webp";

export const Hero = () => {
  const { lang, t } = useLang();
  const words = lang === "en"
    ? ["pride", "passion", "roots", "story"]
    : ["fierté", "passion", "racines", "histoire"];

  const [wordIndex, setWordIndex] = useState(0);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const [splineKey, setSplineKey] = useState(0);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const isMobile = useIsMobile();

  const handleReplay = () => {
    setSplineLoaded(false);
    setSplineKey((k) => k + 1);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (isMobile) return;
    if (window.matchMedia("(hover: none)").matches) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    setParallax({ x, y });
  };

  const handleMouseLeave = () => setParallax({ x: 0, y: 0 });

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [words.length]);

  // Reset index when language switches
  useEffect(() => { setWordIndex(0); }, [lang]);

  return (
    <section
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative overflow-hidden min-h-[90vh] flex items-center bg-foreground"
    >
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        {isMobile ? (
          <img
            src={tunisianCollage}
            alt=""
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center animate-hero-drift will-change-transform"
          />
        ) : (
          <>
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none transition-transform duration-300 ease-out will-change-transform"
              style={{
                transform: `translate3d(${parallax.x * -25}px, ${parallax.y * -18}px, 0)`,
                background:
                  "radial-gradient(circle at 84% 50%, hsl(var(--gold) / 0.75) 0%, hsl(var(--primary) / 0.5) 15%, hsl(var(--primary) / 0.2) 30%, transparent 55%)",
                filter: "blur(50px)",
                animation: "pulseGlow 5s ease-in-out infinite",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none transition-transform duration-500 ease-out will-change-transform mix-blend-screen"
              style={{
                transform: `translate3d(${parallax.x * -12}px, ${parallax.y * -10}px, 0)`,
                background:
                  "radial-gradient(circle at 84% 48%, hsl(var(--gold) / 0.65) 0%, hsl(var(--gold) / 0.25) 12%, transparent 24%)",
                filter: "blur(18px)",
                animation: "pulseGlow 4s ease-in-out infinite reverse",
              }}
            />

            <iframe
              key={splineKey}
              src="https://my.spline.design/nexbotrobotcharacterconcept-fcR7Sr6mKi8YnyF1pgsDRXuL/"
              frameBorder="0"
              loading="eager"
              title="EL FAZA — Robot Nexbot 3D"
              onLoad={() => setSplineLoaded(true)}
              className={`absolute top-0 left-0 h-full transition-opacity duration-1000 will-change-transform ${splineLoaded ? "opacity-100" : "opacity-0"}`}
              style={{
                pointerEvents: "auto",
                width: "130%",
                transform: `translate3d(calc(-3% + ${parallax.x * 8}px), ${parallax.y * 6}px, 0)`,
                transition: "transform 400ms ease-out, opacity 1000ms",
              }}
            />

            <button
              onClick={handleReplay}
              aria-label={t({ fr: "Rejouer la scène 3D", en: "Replay 3D scene" })}
              className="absolute bottom-4 left-4 z-20 group flex items-center gap-2 px-3.5 py-2 rounded-full bg-foreground/40 hover:bg-primary/90 backdrop-blur-md border border-primary-foreground/25 text-primary-foreground text-[11px] font-display font-bold tracking-[0.2em] uppercase shadow-lg transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <RotateCcw className="w-3.5 h-3.5 transition-transform duration-500 group-hover:-rotate-180" />
              <span>Replay</span>
            </button>
          </>
        )}
      </div>

      <div className="absolute inset-0 bg-gradient-to-r from-foreground/85 via-foreground/40 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground/30 via-transparent to-foreground/60 pointer-events-none" />
      <div className="absolute inset-0 bg-zellige opacity-[0.05] pointer-events-none" />

      <div
        aria-hidden
        className="hidden md:block absolute left-0 top-1/4 h-2/3 w-[55%] pointer-events-none z-[1]"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, hsl(var(--foreground)) 0%, hsl(var(--foreground) / 0.92) 35%, hsl(var(--foreground) / 0.6) 60%, transparent 85%)",
        }}
      />

      <div
        className="absolute top-20 left-10 w-28 h-28 rounded-full bg-primary/15 blur-2xl animate-float pointer-events-none transition-transform duration-500 ease-out will-change-transform"
        style={{ transform: `translate3d(${parallax.x * 25}px, ${parallax.y * 20}px, 0)` }}
      />
      <div
        className="absolute bottom-32 right-16 w-36 h-36 rounded-full bg-accent/15 blur-3xl animate-float pointer-events-none transition-transform duration-500 ease-out will-change-transform"
        style={{ animationDelay: "1.5s", transform: `translate3d(${parallax.x * -30}px, ${parallax.y * -22}px, 0)` }}
      />
      <div
        className="absolute top-1/3 right-1/4 w-20 h-20 rounded-full bg-primary/20 blur-xl animate-float pointer-events-none transition-transform duration-500 ease-out will-change-transform"
        style={{ animationDelay: "0.8s", transform: `translate3d(${parallax.x * 18}px, ${parallax.y * -12}px, 0)` }}
      />

      <div className="absolute bottom-0 right-0 z-20 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[220px] h-[60px] bg-gradient-to-tl from-foreground via-foreground/95 to-transparent" />
        <div className="relative m-3 px-3.5 py-2 rounded-full bg-gradient-to-r from-primary via-primary to-accent shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.6)] border border-primary-foreground/25">
          <span className="text-[11px] font-display font-bold text-primary-foreground tracking-[0.2em] uppercase flex items-center gap-1.5">
            <span className="text-accent-foreground">✦</span> EL FAZA <span>🇹🇳</span>
          </span>
        </div>
      </div>

      <div className="container mx-auto px-4 py-28 md:py-44 relative z-10 pointer-events-none">
        <div
          className="max-w-[18rem] sm:max-w-sm md:max-w-md lg:max-w-lg text-center sm:text-left sm:mr-auto sm:ml-0 mx-auto [&_button]:pointer-events-auto [&_a]:pointer-events-auto transition-transform duration-500 ease-out will-change-transform"
          style={{ transform: `translate3d(${parallax.x * -12}px, ${parallax.y * -8}px, 0)` }}
        >
          <div className="animate-fade-in-up relative inline-flex items-center gap-2 mb-8 group/badge">
            <div className="relative bg-primary-foreground/10 backdrop-blur-xl border border-primary-foreground/25 rounded-full px-5 py-2.5 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)]">
              <div
                className="pointer-events-none absolute inset-0 rounded-full opacity-60"
                style={{
                  background: `linear-gradient(135deg, hsl(var(--gold) / 0.6), hsl(var(--primary) / 0.4)) border-box`,
                  WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  padding: "1px",
                }}
              />
              <div className="relative flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-sm font-body font-medium text-primary-foreground tracking-wide">
                  {t({ fr: "Adhésifs 3D premium — Fait main au Canada", en: "Premium 3D stickers — Handcrafted in Canada" })}
                </span>
              </div>
            </div>
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold tracking-tight mb-6 animate-fade-in-up">
            <span className="relative inline-block">
              <span className="text-primary-foreground drop-shadow-[0_2px_25px_rgba(0,0,0,0.5)]">
                EL FAZA
              </span>
              <span aria-hidden className="sparkle-text absolute inset-0">
                EL FAZA
              </span>
            </span>
          </h1>

          <div className="flex items-center gap-3 mb-4 animate-fade-in-up justify-center sm:justify-start" style={{ animationDelay: "0.08s" }}>
            <div className="w-10 h-[2px] rounded-full bg-gradient-to-r from-gold to-primary" />
            <span className="text-[11px] font-body uppercase tracking-[0.25em] font-bold text-gold/90">{t({ fr: "Édition Tunisie · Canada", en: "Tunisia · Canada Edition" })}</span>
          </div>

          <p
            className="text-xl md:text-3xl font-display font-semibold text-primary-foreground mb-2 animate-fade-in-up drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)]"
            style={{ animationDelay: "0.1s" }}
          >
            {t({ fr: "L'art tunisien en adhésif", en: "Tunisian art as a sticker" })}
          </p>

          <div
            className="h-10 md:h-12 overflow-hidden mb-8 animate-fade-in-up"
            style={{ animationDelay: "0.15s" }}
          >
            <p className="text-lg md:text-2xl font-display text-primary-foreground/90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.3)]">
              {lang === "en" ? "Wear your " : "Portez vos "}
              <span
                key={`${lang}-${wordIndex}`}
                className="inline-block text-primary-foreground font-bold border-b-2 border-gold/70 animate-fade-in-up"
              >
                {words[wordIndex]}
              </span>
              {lang === "en" ? " everywhere 🇹🇳" : " partout 🇹🇳"}
            </p>
          </div>

          <p
            className="text-sm md:text-base font-body text-primary-foreground/85 max-w-lg mx-auto sm:mx-0 mb-12 leading-relaxed animate-fade-in-up drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]"
            style={{ animationDelay: "0.2s" }}
          >
            {lang === "en" ? (
              <>
                Each sticker tells a story of Tunisia.
                <br className="hidden md:block" />
                Made in Canada 🍁 with love, pride and gratitude.
              </>
            ) : (
              <>
                Chaque adhésif raconte une histoire de la Tunisie.
                <br className="hidden md:block" />
                Créé au Canada 🍁 avec amour, fierté et gratitude.
              </>
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-start animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
            <Button
              size="lg"
              className="font-display text-base px-10 py-6 rounded-full shadow-[0_12px_30px_-8px_hsl(var(--primary)/0.6)] hover:shadow-[0_18px_40px_-8px_hsl(var(--primary)/0.8)] transition-all duration-300 hover:scale-105"
              onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
            >
              {t({ fr: "Découvrir nos adhésifs", en: "Shop our stickers" })}
              <ArrowDown className="w-4 h-4 ml-1" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="font-display text-base px-8 py-6 rounded-full bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/20 backdrop-blur-md"
              onClick={() => {
                const el = document.querySelector("[data-newsletter]");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {t({ fr: "Restez à jour 📬", en: "Stay in the loop 📬" })}
            </Button>
          </div>

          <div
            className="mt-16 animate-fade-in-up"
            style={{ animationDelay: "0.5s" }}
          >
            <div className="relative inline-flex items-stretch rounded-2xl bg-primary-foreground/[0.08] backdrop-blur-xl border border-primary-foreground/20 px-2 py-3 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.5)]">
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl opacity-60"
                style={{
                  background: `linear-gradient(135deg, hsl(var(--gold) / 0.5), hsl(var(--primary) / 0.3)) border-box`,
                  WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  padding: "1px",
                }}
              />
              {[
                { value: "3D", label: t({ fr: "Relief premium", en: "Premium relief" }) },
                { value: "Canada", label: t({ fr: "Fabriqué ici", en: "Made here" }) },
                { value: "🇹🇳", label: t({ fr: "Fierté tunisienne", en: "Tunisian pride" }) },
              ].map((stat, i) => (
                <div key={stat.label} className="relative flex items-center">
                  {i > 0 && <div className="w-px h-10 bg-primary-foreground/20 self-center" />}
                  <div className="px-5 md:px-7 text-center group">
                    <div className="text-xl md:text-2xl font-display font-bold text-primary-foreground drop-shadow-md group-hover:scale-110 transition-transform">
                      {stat.value}
                    </div>
                    <div className="text-[10px] md:text-xs font-body text-primary-foreground/70 mt-1 uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-primary-foreground/40 flex items-start justify-center p-1.5">
          <div className="w-1.5 h-3 bg-primary-foreground/60 rounded-full animate-float" />
        </div>
      </div>
    </section>
  );
};
